import React from 'react';

import Box from '@material-ui/core/Box';
import Link from '@material-ui/core/Link';
import { Alert } from '@material-ui/lab';

import { ContentSection } from './ContentSection';
import { InfoTooltip } from './InfoTooltip';
import { InlineField } from './InlineField';
import { SectionItem } from './SectionItem';
import { StatusIndicator } from './StatusIndicator';
import { ConditionEntry, CreationStatus } from './types';
import { SUPPORT_URL } from './utils';

export interface CreationStatusPanelProps {
  kind: 'Application' | 'ApplicationSet';
  creationStatus: CreationStatus;
  creationStatusMessage?: string;
  conditions?: ConditionEntry[];
}

/**
 * Renders the Pending/Failed creation panels shared by Application and
 * ApplicationSet cards. Returns null for any other status so callers can render
 * their own content for the succeeded path.
 */
export const CreationStatusPanel = ({
  kind,
  creationStatus,
  creationStatusMessage,
  conditions,
}: CreationStatusPanelProps) => {
  if (creationStatus === 'Pending') {
    return (
      <>
        <ContentSection
          label={`${kind} Creation Status`}
          gridSizes={{ xs: 12 }}
        >
          <SectionItem>
            <InlineField label="Status">
              <StatusIndicator type="pending" label="Pending" />
            </InlineField>
          </SectionItem>
        </ContentSection>
        <Alert severity="warning" style={{ marginBottom: 16 }}>
          Provisioning is in progress and may take up to 5 minutes. Should it
          take longer, please <Link href={SUPPORT_URL}>contact us</Link>.
        </Alert>
      </>
    );
  }

  if (creationStatus === 'Failed') {
    return (
      <>
        <ContentSection
          label={`${kind} Creation Status`}
          gridSizes={{ xs: 12 }}
        >
          <SectionItem>
            <InlineField label="Status">
              <StatusIndicator type="failed" label="Failed" />
            </InlineField>
          </SectionItem>
        </ContentSection>
        <Alert severity="error" style={{ marginBottom: 8 }}>
          <strong>{kind} creation failed</strong>
          <Box
            component="pre"
            style={{
              margin: '8px 0 0',
              padding: 8,
              fontSize: 12,
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              backgroundColor: 'rgba(0,0,0,0.04)',
              borderRadius: 4,
            }}
          >
            {creationStatusMessage || 'Unknown error'}
          </Box>
        </Alert>
        {conditions?.map((c, i) => (
          <Alert key={i} severity="error" style={{ marginBottom: 4 }}>
            <strong>{c.type}:</strong> {c.message}
          </Alert>
        ))}
        <Alert severity="info" style={{ marginBottom: 16 }}>
          If this error is unexpected, see our{' '}
          <Link href={SUPPORT_URL}>support page</Link> for help.
        </Alert>
      </>
    );
  }

  return null;
};

interface ProblemTooltipProps {
  problems: Array<{ name: string; reason: string }>;
}

export const ProblemTooltip = ({ problems }: ProblemTooltipProps) => {
  if (problems.length === 0) return null;
  return (
    <InfoTooltip
      message={problems.map(p => `${p.name}: ${p.reason}`).join('\n')}
    />
  );
};
